import { DocumentReference, Timestamp } from "firebase/firestore";
import { FeatureFlag } from "./featureFlags";
import { SigilDocument } from "./sigil";
import { UploadProduct } from "./products";
import { User } from "firebase/auth";

export type WebsiteLinks = {
  home: string;
  products: string;
  pricing: string;
  about: string;
  caseStudies: string;
  resources: string;
  privacy: string;
  websiteTerms: string;
  productTerms: string;
};

export type SigilLinks = {
  calendar: string;
  demoCalendar: string;
  enrollPageVideo: string;
  googleReview: string;
  linkedIn: string;
  pricing: string;
  salesEmail: string;
  supportEmail: string;
  customerSuccess: string;
  website: WebsiteLinks;
};

export const DEFAULT_SIGIL_LINKS: SigilLinks = {
  calendar: "https://calendly.com/ashley-sigil/30min",
  demoCalendar: "https://calendly.com/sigilprotects/demo",
  enrollPageVideo:
    "https://demo.arcade.software/UCYcTs3FHW1WRPMglFRP?embed&embed_mobile=tab&embed_desktop=inline&show_copy_link=true",
  googleReview: "https://g.page/r/CdlY1uLnWrEyEBM/review",
  linkedIn: "https://www.linkedin.com/company/sigil-inc/",
  pricing: "https://sigilprotects.com/plans-and-pricing/",
  salesEmail: "zac@sigilprotects.com",
  supportEmail: "support@sigilprotects.com",
  customerSuccess: "kelsey@sigilprotects.com",
  website: {
    home: "https://sigilprotects.com/",
    products: "https://sigilprotects.com/products/",
    pricing: "https://sigilprotects.com/plans-and-pricing/",
    about: "https://sigilprotects.com/about-us/",
    caseStudies: "https://sigilprotects.com/case-studies/",
    resources: "https://sigilprotects.com/resources/",
    privacy: "https://sigilprotects.com/privacy-policy/",
    websiteTerms: "https://sigilprotects.com/website-terms-of-service/",
    productTerms: "https://sigilprotects.com/product-terms-of-service/",
  },
};

export type SigilFAQ = {
  q: string;
  a: string;
};

export const DEFAULT_SIGIL_FAQS: SigilFAQ[] = [
  {
    q: "Is there a credit card required to start?",
    a: "No. We only need your email and brand details. No payment unless you decide to upgrade.",
  },
  {
    q: "Will you spam me with emails? I don’t want sales or marketing emails.",
    a: "No, only our Customer Success team will reach out to make sure your free trial is working properly. You will not receive any sales or marketing emails, and the Customer Success team will not try to “upsell” you.",
  },
  {
    q: "What happens after my free trial ends?",
    a: "You simply won’t be able to log in any more. We will keep your data for 180 days in case you change your mind.",
  },
  {
    q: "Can I take action against any unauthorized sellers?",
    a: "Only our paid plans include active enforcement. We cannot file enforcements without first confirming your identity and that you have the authority to file enforcements. That requires meeting with us and setting up a paid plan. The trial gives you tools to see the problem, prioritize your time, and try to address it yourself.",
  },
  {
    q: "If I want to upgrade, how much will it cost?",
    a: "You can see full details on our pricing plans. You need to pick a monitoring plan (based on SKU, starting as low as $150/mo for 500 SKUs), and then purchase enforcement credits (1 credit = full enforcement against 1 seller). You can estimate how many credits you will need after seeing your sellers in the free trial. Credits start at $75 each, but get cheaper as you buy bundles. Most brands buy a bundle of 10 for $600 when they start. Credits do not expire—use them as you need them.",
  },
  {
    q: "If I have questions, can I talk to someone?",
    a: "Of course. You can email our customer success team at CUSTOMER_SUCCESS_EMAIL, or book a call directly from the Talk to an Expert button above.",
  },
];
export interface EmailNotificationSettings {
  weekly: boolean;
  monthly: boolean;
}
export interface TenantConfig {
  tenant: string;
  name: string;
  ref: DocumentReference;
  createdAtUtc: Timestamp;
  props: {
    activityDashboardId: number;
    address1: string;
    address2: string;
    city: string;
    phone: string;
    state: string;
    zip: string;
    faqs: SigilFAQ[];
    featureFlags: FeatureFlag[];
    casesLaunched: number;
    planRemovals: number;
    costPerRemoval: number;
    resetDateUtc: Timestamp;
    batchCount: number;
    ignoredGracePeriodDays: number;
    customBatchRules: string[];
    currentPlan: string;
    links: SigilLinks;
    submissionAccount?: string;
  };
}

export interface UserConfig {
  tenant: string;
  ref: DocumentReference;
  user: string;
  createdAtUtc: string;
  props: {
    disabledAnnouncements: string[];
    isCasesExpanded: boolean;
    onboarding: {
      currentStep: string;
      survey: {
        companyName?: string;
        goals?: string[];
        referralSource?: string;
        marketplace?: string;
        customMarketplace?: string;
        tenant?: string;
      };
      productCatalog: UploadProduct[];
    };
    emailNotifications?: EmailNotificationSettings;
  };
}

export interface GlobalConfigState {
  tenant: TenantConfig;
  user: UserConfig;
  configReady: boolean;
}
export interface GlobalConfigType {
  unSub: () => void;
  loadConfig: (user: User, tenant: string) => Promise<void>;
  globalConfig: GlobalConfigState;
}

export type BrandConfigDocument = SigilDocument<
  BrandProps,
  {
    status: "UNVERIFIED" | "VERIFIED";
    marketSearches: MarketSearch[];
    identifiers: string[];
  }
>;

interface BrandProps {
  allowAboveMapAuthorizedCases: boolean;
  allowCreateProduct: boolean;
  authorizedSellerDiscountPercent: number;
  defaultMapPrice: number;
  defaultTrademark: string;
  integration: {
    maxListingCount: number;
    productConfigurations: ProductConfiguration[];
    seekerConfigurations: string[];
  };
  map: {
    templates: {
      body: string;
      id: string;
      name: string;
      subject: string;
      violation: number;
    }[];
  };
  preDsm: {
    enabled: boolean;
    excludedSellerIdentifiers: string[];
    templates: EmailTemplate[];
  };
  qtyEstRefreshDays: number;
  removals: {
    excludedMarkets: string[];
  };
  salesEstRefreshDays: number;
  unauthorizedSellerPriceThreshold: number;
}

export interface MarketSearch {
  market: string;
  config: string;
}

interface ProductConfiguration {
  allow_all_data_processing: boolean;
  collection_id: string;
  listing_watch_list: string[];
  seeker: string;
}

interface EmailTemplate {
  body: string;
  id: string;
  name: string;
  subject: string;
  violation: number;
}
